"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { useState, useEffect } from "react";
import { useAppStore, currentView } from "@/lib/store";
import { apiGet } from "@/lib/api-client";
import { setAppCurrency } from "@/components/booking/format";
import { I18nProvider } from "@/lib/i18n/provider";
import type { Locale } from "@/lib/i18n";
import { isLocale } from "@/lib/i18n";

function AuthBootstrap() {
  const setUser = useAppStore((s) => s.setUser);
  const setAuthLoading = useAppStore((s) => s.setAuthLoading);
  const setImpersonating = useAppStore((s) => s.setImpersonating);
  const setPermissions = useAppStore((s) => s.setPermissions);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiGet<{ user: any; impersonating?: boolean; permissions?: string[] }>(
          "/api/auth/me"
        );
        if (mounted) {
          setUser(res.user ?? null);
          setImpersonating(!!res.impersonating);
          setPermissions(res.permissions ?? []);
        }
      } catch {
        if (mounted) {
          setUser(null);
          setImpersonating(false);
          setPermissions([]);
        }
      } finally {
        if (mounted) setAuthLoading(false);
      }
      // Load the app-wide display currency (public endpoint, EUR fallback).
      try {
        const settings = await apiGet<{ currency: string }>("/api/settings");
        if (mounted && settings.currency) setAppCurrency(settings.currency);
      } catch {
        /* keep default EUR */
      }
    })();
    return () => {
      mounted = false;
    };
  }, [setUser, setAuthLoading, setImpersonating, setPermissions]);
  return null;
}

export function Providers({
  initialLocale,
  children,
}: {
  initialLocale?: Locale;
  children: React.ReactNode;
}) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const locale: Locale =
    initialLocale && isLocale(initialLocale) ? initialLocale : "en";

  return (
    <QueryClientProvider client={client}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange
      >
        <I18nProvider initialLocale={locale}>
          <AuthBootstrap />
          {children}
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
